import React, { useContext, useMemo, useState } from "react";
import UpDownButton from "../../Buttons/UpDownButton";
import UserContext, { settingsAliases } from "../../../contexts/User";
import TableComponent from "../../TableComponent";
import moment from "moment";
import { PdfHeader } from "./BasketViz";
import MeasuresContext from "../../../contexts/Measures";

/**
 * @typedef {Object} KpiSettings
 * @property {boolean} [flag_ser]
 * @property {boolean} [flag_bre]
 * @property {boolean} [flag_ins]
 * @property {boolean} [flag_tyr]
 * @property {boolean} [flag_fin]
 *
 * @typedef {Object} KpiObject
 * @property {string} [name]
 * @property {number} [id]
 */

/**
 * @param {Object} props
 * @param {KpiSettings} [props.userPref]
 * @param {KpiObject} [props.kpi]
 * @param {string} [props.ctryid]
 */
const BasketSummary = ({ userPref = {}, kpi = {}, ctryid = undefined, ...props }) => {
    let summaryLabelsIterator = Object.entries(settingsAliases);
    const servicesStmt = [1, 4].includes(kpi?.id) ? summaryLabelsIterator.map(([key, labelObj], index) => (userPref[key] ? "Incl." : "Excl.") + " " + (labelObj[ctryid] || labelObj["default"]) + (index > summaryLabelsIterator?.length - 1 ? ", " : " ")) : "";

    return <span className="italic text-gray-500 text-xs font-medium">{`Monthly Rate Incl. VAT, LCV Rental Excl. VAT,  ${kpi?.name}, ${servicesStmt}`}</span>;
};

const Summary = ({ columns, filters, dataTable, selectedBaskets, basket, printStatus, printMode, ...props }) => {
    const { user } = useContext(UserContext);
    const { selectedKpi } = useContext(MeasuresContext);
    const [showSynthesis, setShowSynthesis] = useState(user?.basket_sections?.Basket_Summary);

    // Get filters for header display
    const filterMthCode = filters[5];
    const filterCountryId = filters[2];
    const filterChannel1 = filters[1];
    const filterDuration = filters[3];

    // Format date for header
    const monthDate = filterMthCode ? moment(filterMthCode.positiveAttributeFilter.in["values"][0], "YYYY MM").format("MMMM YY") : "";

    // Get country for header
    const country = props.getCountryByCode ? props.getCountryByCode(filterCountryId?.positiveAttributeFilter?.in["values"][0]) : filterCountryId?.positiveAttributeFilter?.in["values"][0];

    // Check if this component should be visible in current print mode
    const showInPrintMode = !printStatus || printMode === "all" || printMode === "summary";

    const memoizedDataTable = useMemo(() => {
        if (!dataTable) return [];
        return dataTable.map((row, index) => ({
            ...row,
            key: row.id || `${row.basket}-${row.versionStd}-${index}`,
        }));
    }, [dataTable]);

    // Remove basket column and filter columns to only show those with data
    const filteredColumns = useMemo(() => {
        if (!dataTable || dataTable.length === 0) {
            return columns.filter((col) => col.dataIndex !== "basket");
        }

        // Fixed columns that should always be shown
        const fixedColumns = ["versionStd", "average"];

        // Find which columns have at least one non-null value in the data
        const columnsWithData = new Set();
        dataTable.forEach((row) => {
            Object.keys(row).forEach((key) => {
                if (row[key] !== null && row[key] !== undefined) {
                    columnsWithData.add(key);
                }
            });
        });

        // Filter columns: remove basket, and only include fixed columns or columns with data
        return columns.filter((col) => {
            if (col.dataIndex === "basket") return false;
            // Always include fixed columns
            if (fixedColumns.includes(col.dataIndex)) return true;
            // Only include other columns if they have data
            return columnsWithData.has(col.dataIndex);
        });
    }, [columns, dataTable]);

    if (!showInPrintMode) return null;

    // Create print-optimized columns with larger font size
    const printColumns = printStatus
        ? filteredColumns.map((col) => ({
              ...col,
              title: typeof col.title === "string" ? <span style={{ fontSize: "12px", fontWeight: "bold" }}>{col.title}</span> : col.title, // Preserve non-string titles (like React elements or images)
              render: col.render
                  ? (text, record) => {
                        const originalContent = col.render(text, record);
                        // If the rendered content is a string or number, wrap it in a span with adjusted font
                        if (typeof originalContent === "string" || typeof originalContent === "number") {
                            return <span style={{ fontSize: "11px" }}>{originalContent}</span>;
                        }
                        // Otherwise, return the original content
                        return originalContent;
                    }
                  : (text) => <span style={{ fontSize: "11px" }}>{text}</span>,
          }))
        : filteredColumns;

    return (
        <div className={printStatus ? "break-after-page" : "my-4"}>
            {/* Print styles */}
            {printStatus && (
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                        @media print {
                            .ant-table { font-size: 11px !important; }
                            .ant-table-thead > tr > th { 
                                padding: 6px 8px !important; 
                                height: 24px !important;
                                background-color: #f0f0f0 !important;
                                -webkit-print-color-adjust: exact !important;
                                color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                font-weight: bold !important;
                                border: 1px solid #ddd !important;
                            }
                            .ant-table-tbody > tr > td { 
                                padding: 4px 8px !important; 
                                height: 20px !important; 
                                font-size: 11px !important;
                                border: 1px solid #ddd !important;
                            }
                            .summary-print-header {
                                margin-bottom: 10px !important;
                                font-size: 16px !important;
                            }
                            .ant-table-wrapper { 
                                margin-top: 8px !important;
                                margin-bottom: 20px !important;
                            }
                            /* Ensure borders are visible when printing */
                            table, th, td {
                                border-collapse: collapse !important;
                                border: 1px solid #ddd !important;
                            }
                            /* Force background colors to print */
                            * {
                                -webkit-print-color-adjust: exact !important;
                                color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                        }
                    `,
                    }}
                />
            )}

            {/* PDF Header - only shown in print mode */}
            {printStatus && (
                <PdfHeader
                    basketName={basket?.name || selectedBaskets[0]?.name || ""}
                    country={country}
                    date={monthDate}
                    channel1={filterChannel1.positiveAttributeFilter.in["values"][0]}
                    duration={filterDuration.positiveAttributeFilter.in["values"][0]}
                    kpi={selectedKpi?.name}
                    adjdeposititle={props.adjdeposititle}
                />
            )}

            {/* Normal render for non-print mode */}
            {!printStatus && (
                <div className="p-3 rounded-md flex justify-between items-stretch cursor-pointer hover:bg-gray-200" onClick={() => setShowSynthesis(!showSynthesis)}>
                    <span className="font-semibold text-lg text-steel">Summary Table</span>
                    <UpDownButton visibilityState={showSynthesis} />
                </div>
            )}

            {/* Title for print mode */}
            {printStatus && <h3 className="text-xl font-bold text-center mb-2 summary-print-header">Summary Table</h3>}

            {/* Table content - always shown in print mode */}
            {(showSynthesis || printStatus) && (
                <>
                    <TableComponent
                        enableScreenshot={!printStatus}
                        columns={printColumns}
                        dataSource={memoizedDataTable}
                        pagination={false}
                        scroll={printStatus ? {} : { y: 400 }}
                        bordered
                        className={`border border-steel-300 ${printStatus ? "print-table" : "mt-16"}`}
                        widgetProps={{
                            id: "summary-table",
                            filters: filters,
                            section: "basketsummary",
                            metadata: { selectedBaskets },
                        }}
                        {...props}
                    />
                </>
            )}
        </div>
    );
};

export { Summary, BasketSummary };
